import type { Directive, DirectiveBinding } from 'vue'

/**
 * Custom directive to render trusted HTML content including scripts and event handlers.
 * This directive allows execution of <script> tags and inline event handlers (onclick, etc.)
 * from trusted data sources.
 *
 * WARNING: Only use with trusted data sources! This bypasses Vue's security sanitization.
 *
 * Usage:
 * <div v-trusted-html="htmlContent"></div>
 */

interface TrustedHtmlElement extends HTMLElement {
  _trustedHtmlCleanup?: () => void
}

export const vTrustedHtml: Directive = {
  mounted(el: TrustedHtmlElement, binding: DirectiveBinding<string>) {
    updateContent(el, binding.value)
  },

  updated(el: TrustedHtmlElement, binding: DirectiveBinding<string>) {
    if (binding.value !== binding.oldValue) {
      cleanup(el)
      updateContent(el, binding.value)
    }
  },

  unmounted(el: TrustedHtmlElement) {
    cleanup(el)
  },
}

function updateContent(el: TrustedHtmlElement, html: string) {
  if (!html) {
    el.innerHTML = ''
    return
  }

  // Set the HTML content
  el.innerHTML = html

  // Execute any script tags
  executeScripts(el)

  // Bind event handlers
  bindEventHandlers(el)

  // Store cleanup function
  el._trustedHtmlCleanup = () => {
    // Cleanup will be handled by Vue when elements are removed
  }
}

function executeScripts(container: HTMLElement) {
  // Find all script tags
  const scripts = container.querySelectorAll('script')

  scripts.forEach((oldScript) => {
    // Create a new script element (needed for execution)
    const newScript = document.createElement('script')

    // Copy attributes
    Array.from(oldScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value)
    })

    // Copy content
    newScript.textContent = oldScript.textContent

    // Replace old script with new one to trigger execution
    oldScript.parentNode?.replaceChild(newScript, oldScript)
  })
}

function bindEventHandlers(container: HTMLElement) {
  // Find all elements with inline event handlers
  const allElements = container.querySelectorAll('*')

  allElements.forEach((element) => {
    Array.from(element.attributes).forEach((attr) => {
      // Check if attribute is an event handler (starts with 'on')
      if (attr.name.startsWith('on') && attr.value) {
        const eventName = attr.name.substring(2).toLowerCase() // Remove 'on' prefix
        const handlerCode = attr.value

        try {
          // Create function from the handler code
          // eslint-disable-next-line no-new-func
          const handler = new Function('event', handlerCode)

          // Add event listener
          element.addEventListener(eventName, handler as EventListener)

          // Remove the inline handler attribute to prevent it from being called twice
          element.removeAttribute(attr.name)
        } catch (error) {
          console.error(`Error binding event handler for ${attr.name}:`, error)
        }
      }
    })
  })
}

function cleanup(el: TrustedHtmlElement) {
  if (el._trustedHtmlCleanup) {
    el._trustedHtmlCleanup()
    delete el._trustedHtmlCleanup
  }
}

// Export for plugin registration
export default {
  install(app: any) {
    app.directive('trusted-html', vTrustedHtml)
  },
}
